/**
 * Page Navigation — sticky under fixed header + scroll-spy active links.
 */

const getHeaderOffset = () => {
  const header = document.querySelector('.header');
  if (!header) {
    return window.matchMedia('(min-width: 1024px)').matches ? 135 : 125;
  }

  return Math.round(header.getBoundingClientRect().bottom);
};

const setCssOffsets = (navHeight) => {
  const headerOffset = getHeaderOffset();
  document.documentElement.style.setProperty('--header-offset', `${headerOffset}px`);
  document.documentElement.style.setProperty('--page-nav-height', `${navHeight}px`);
};

const initPageNavigation = () => {
  const section = document.querySelector('[data-page-navigation]');
  if (!section) {
    return;
  }

  const bar = section.querySelector('.page-navigation__bar');
  const links = Array.from(
    section.querySelectorAll('[data-page-navigation-link]')
  );
  if (!bar || !links.length) {
    return;
  }

  document.documentElement.classList.add('has-page-navigation');

  const targets = links
    .map((link) => {
      const id = link.getAttribute('href')?.replace(/^#/, '') || '';
      const el = id ? document.getElementById(id) : null;
      return el ? { link, el, id } : null;
    })
    .filter(Boolean);

  let pinned = false;
  let placeholder = null;
  let ticking = false;

  const syncPlaceholder = () => {
    if (placeholder) {
      placeholder.style.height = `${bar.offsetHeight}px`;
    }
  };

  const pin = () => {
    if (pinned) {
      return;
    }

    placeholder = document.createElement('div');
    placeholder.className = 'page-navigation__placeholder';
    placeholder.style.height = `${bar.offsetHeight}px`;
    placeholder.setAttribute('aria-hidden', 'true');
    section.insertBefore(placeholder, bar);

    bar.classList.add('is-sticky');
    bar.style.top = `${getHeaderOffset()}px`;
    pinned = true;
    setCssOffsets(bar.offsetHeight);
  };

  const unpin = () => {
    if (!pinned) {
      return;
    }

    bar.classList.remove('is-sticky');
    bar.style.top = '';
    if (placeholder) {
      placeholder.remove();
      placeholder = null;
    }
    pinned = false;
    setCssOffsets(bar.offsetHeight);
  };

  const updateSticky = () => {
    const headerOffset = getHeaderOffset();
    const sectionTop = section.getBoundingClientRect().top;

    if (sectionTop <= headerOffset) {
      pin();
      bar.style.top = `${headerOffset}px`;
      syncPlaceholder();
    } else {
      unpin();
    }

    setCssOffsets(bar.offsetHeight);
  };

  const setActive = (id) => {
    links.forEach((link) => {
      const linkId = link.getAttribute('href')?.replace(/^#/, '') || '';
      link.classList.toggle('is-active', linkId === id);
      if (linkId === id) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const updateActiveFromScroll = () => {
    const offset = getHeaderOffset() + bar.offsetHeight + 24;
    let currentId = targets[0]?.id || '';

    for (const item of targets) {
      const top = item.el.getBoundingClientRect().top;
      if (top <= offset) {
        currentId = item.id;
      } else {
        break;
      }
    }

    if (currentId) {
      setActive(currentId);
    }
  };

  const onScrollOrResize = () => {
    if (ticking) {
      return;
    }
    ticking = true;
    window.requestAnimationFrame(() => {
      updateSticky();
      updateActiveFromScroll();
      ticking = false;
    });
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href')?.replace(/^#/, '') || '';
      const target = document.getElementById(id);
      if (!target) {
        return;
      }

      event.preventDefault();

      // Pin first so offset accounts for sticky bar height.
      updateSticky();
      const offset = getHeaderOffset() + bar.offsetHeight + 12;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top, behavior: 'smooth' });
      history.pushState(null, '', `#${id}`);
      setActive(id);
    });
  });

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  updateSticky();
  updateActiveFromScroll();

  // Honor hash on load after layout settles.
  if (window.location.hash) {
    const hashId = window.location.hash.replace(/^#/, '');
    const hashTarget = document.getElementById(hashId);
    if (hashTarget) {
      window.requestAnimationFrame(() => {
        updateSticky();
        const offset = getHeaderOffset() + bar.offsetHeight + 12;
        const top = hashTarget.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'auto' });
        setActive(hashId);
      });
    }
  }
};

export default initPageNavigation;
